import React, { useEffect, useState } from 'react';
// prettier-ignore
import { CircularProgress, Divider, Typography, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CostCenterUserDetails } from './CostCenterUserDetails.jsx';
import { AddDialog } from '../shared/AddDialog.jsx';
import AddIcon from '@mui/icons-material/Add';
import { ErrorFlow } from 'olympe';

export const CostCenterChildAccordion = ({
    setOpenAlert,
    setOpenErrorAlert,
    setErrorMessage,
    itemId,
    itemDescription,
    childAccordionDetails,
    childAccordionIdx,
    costCenter,
    priceRange,
    childAccordionExpanded,
    setChildAccordionExpanded,
    fetchLevel2BasedOnLevel1,
    triggerChange,
    setTriggerChange,
    context,
}) => {
    const operation = context.get('Operation');
    const getApprovers = context.get('Get Approver');
    const addApprover = context.get('Post Add Approver');
    const deleteApprover = context.get('Post Delete Approver');
    const editApprover = context.get('Post Change Approver');
    const [levelApproversDetails, setLevelApproversDetails] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const getItemType = () => (operation === 'PO' ? 'Purchase Group' : 'Cost Center');

    const fetchApprovers = () => {
        const [triggerInput, itemIdInput, relGroup, relStrategy, relCode] = getApprovers.getInputs();

        // eslint-disable-next-line no-unused-vars
        const [Success, Error, array] = getApprovers.getOutputs();

        setLoading(true);
        const getApprovers$ = context
            .runner(getApprovers)
            .set(itemIdInput, itemId)
            .set(relGroup, childAccordionDetails.RelGroup)
            .set(relStrategy, childAccordionDetails.RelStrategy)
            .set(relCode, childAccordionDetails.RelCode)
            .trigger(triggerInput);

        getApprovers$.waitFor(Success).then(() => {
            setLevelApproversDetails(getApprovers$.get(array));
            setLoading(false);
        });
    };

    const handleAddApprover = (approverAdded, addedOrderCategory, type) => (event) => {
        const [triggerInput, itemIdInput, relGroup, relCode, orderCategory, agentType, userId, positionId] =
            addApprover.getInputs();
        const [Success, Error] = addApprover.getOutputs();

        const addApprover$ = context
            .runner(addApprover)
            .set(itemIdInput, itemId)
            .set(relGroup, childAccordionDetails.RelGroup)
            .set(relCode, childAccordionDetails.RelCode)
            .set(orderCategory, addedOrderCategory?.Auftyp)
            .set(agentType, type === 'US' ? 'US' : 'S')
            .set(userId, approverAdded.ApproverId || approverAdded.PosId)
            .set(positionId, approverAdded.PositionId || approverAdded.PosId)
            .trigger(triggerInput);

        Promise.any([addApprover$.waitFor(Success), addApprover$.waitFor(Error)]).then(handleDialogChangeResponse);
    };

    const handleDeleteApprover = (deletedApprover, deletedOrderCategory) => {
        const [triggerInput, itemIdInput, relGroup, relCode, orderCategory, agentType, agentId] =
            deleteApprover.getInputs();

        const [Success, Error] = deleteApprover.getOutputs();

        const deleteApprover$ = context
            .runner(deleteApprover)
            .set(itemIdInput, itemId)
            .set(relGroup, childAccordionDetails.RelGroup)
            .set(relCode, childAccordionDetails.RelCode)
            .set(orderCategory, deletedOrderCategory)
            .set(agentType, isNaN(deletedApprover) ? 'US' : 'S')
            .set(agentId, deletedApprover)
            .trigger(triggerInput);

        Promise.any([deleteApprover$.waitFor(Success), deleteApprover$.waitFor(Error)]).then(
            handleDialogChangeResponse
        );
    };

    const handleEditApprover = (oldApprover, oldOrderCat, newApprover, newOrderCat, type) => {
        const [
            triggerInput,
            itemIdInput,
            relGroup,
            relCode,
            orderCategory,
            agentType,
            agentId,
            userId,
            oldOrderCategory,
            positionId,
        ] = editApprover.getInputs();

        const [Success, Error] = editApprover.getOutputs();

        const editApprover$ = context
            .runner(editApprover)
            .set(itemIdInput, itemId)
            .set(relGroup, childAccordionDetails.RelGroup)
            .set(relCode, childAccordionDetails.RelCode)
            .set(orderCategory, newOrderCat)
            .set(agentType, type === 'US' ? 'US' : 'S')
            .set(agentId, oldApprover)
            .set(userId, newApprover.ApproverId || newApprover.PosId)
            .set(oldOrderCategory, oldOrderCat)
            .set(positionId, newApprover.PositionId || newApprover.PosId)
            .trigger(triggerInput);

        Promise.any([editApprover$.waitFor(Success), editApprover$.waitFor(Error)]).then(handleDialogChangeResponse);
    };

    const handleClickOpenDialog = () => {
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
    };

    const handleDialogChangeResponse = (val) => {
        const success = !(val instanceof ErrorFlow);
        setOpen(false);
        setOpenAlert(success);
        if (!success) {
            setErrorMessage(val.getMessage());
            setOpenErrorAlert(true);
        }
        // else { TODO can change behaviour after UI rework
        fetchLevel2BasedOnLevel1();
        // }
        setTriggerChange(!triggerChange);
    };

    const handleExpandChange = (panel) => (event, isExpanded) => {
        if (isExpanded === true) {
            fetchApprovers();
            const newState = childAccordionExpanded;
            const newPriceRangeState = childAccordionExpanded.get(priceRange);
            newPriceRangeState[childAccordionIdx] = true;
            newState.set(priceRange, newPriceRangeState);
            setChildAccordionExpanded(newState);
        } else {
            setLevelApproversDetails([]);
            const newState = childAccordionExpanded;
            const newPriceRangeState = childAccordionExpanded.get(priceRange);
            newPriceRangeState[childAccordionIdx] = false;
            newState.set(priceRange, newPriceRangeState);
            setChildAccordionExpanded(newState);
        }
        event.stopPropagation();
    };

    useEffect(() => {
        if (childAccordionExpanded.get(priceRange)[childAccordionIdx] === true) {
            fetchApprovers();
        }
    }, [triggerChange]);

    return (
        <Accordion
            className={'accordion'}
            sx={{ boxShadow: 'none' }}
            key={childAccordionIdx}
            disableGutters={true}
            defaultExpanded={childAccordionExpanded.get(priceRange)[childAccordionIdx]}
            expanded={childAccordionExpanded.get(priceRange)[childAccordionIdx]}
            onChange={handleExpandChange('panel' + childAccordionIdx)}
            TransitionProps={{ unmountOnExit: true }}
        >
            <AccordionSummary
                className={'accordion-summary'}
                expandIcon={<ExpandMoreIcon size="large" sx={{ color: 'rgb(102, 110, 114)' }} />}
                classes={{
                    content: 'accordion-summary-no-margin',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flex: 1,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: '500', color: 'rgb(102, 110, 114)' }}>
                            Release group / Release code
                        </Typography>
                        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: '600', marginLeft: '1vw' }}>
                            ({childAccordionDetails.RelGroup}/{childAccordionDetails.RelCode})
                        </Typography>
                        {levelApproversDetails?.length > 0 ? (
                            <Typography variant="body2" sx={{ color: 'rgb(102, 110, 114)', marginLeft: '1vw' }}>
                                {levelApproversDetails.length} approvers
                            </Typography>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails sx={{ width: 'auto', padding: '0px 13px 8px' }}>
                {loading ? (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <CircularProgress color="primary" sx={{ margin: '0 1vw 0 0' }} />
                        <Typography variant="body1" color="primary">
                            Loading...
                        </Typography>
                    </div>
                ) : (
                    levelApproversDetails?.map((obj, idx) => {
                        return (
                            <CostCenterUserDetails
                                key={idx + obj.CostElementId + obj.UserId}
                                itemDescription={itemDescription}
                                userDetails={obj}
                                userIndex={idx}
                                handleDeleteApprover={handleDeleteApprover}
                                handleEditApprover={handleEditApprover}
                                context={context}
                            />
                        );
                    })
                )}
                <div
                    className={'flex-row-container-button'}
                    // add button row is placed absolutely above the other rows, on top of the AccordionSummary, to gain space
                    // TODO this is acceptable quick fix, until the accordion design is reviewed
                    style={{ position: 'absolute', right: '13px', top: '1px' }}
                >
                    <IconButton
                        color="primary"
                        aria-label="add"
                        component="div"
                        size="small"
                        disabled={localStorage.getItem('edit') !== 'true'}
                        sx={{
                            color: 'rgb(102, 110, 114)',
                            backgroundColor: '#FFF',
                            borderRadius: '20px',
                            padding: '0px 23px',
                            border: 'dashed 1px rgb(102, 110, 114)',
                            '&:hover': {
                                backgroundColor: 'rgb(102, 110, 114)',
                                boxShadow: 'none',
                                color: '#FFF',
                            },
                        }}
                        onClick={handleClickOpenDialog}
                    >
                        <AddIcon fontSize="small" />
                        <Typography variant="body2">Add</Typography>
                    </IconButton>
                </div>
                {open && (
                    <AddDialog
                        open={open}
                        title={getItemType()}
                        itemDescription={itemDescription}
                        itemId={itemId}
                        childAccordionDetails={childAccordionDetails}
                        childAccordionIdx={childAccordionIdx}
                        handleCloseDialog={handleCloseDialog}
                        handleAddApprover={handleAddApprover}
                        context={context}
                    />
                )}
            </AccordionDetails>
            <Divider sx={{ width: '100%', color: 'rgb(102, 110, 114)' }} />
        </Accordion>
    );
};
